import { useState, useCallback, useRef, SyntheticEvent } from 'react';
import { RegPlateInput } from '../RegPlateInput/RegPlateInput';
import { IconChevronDown } from '@tabler/icons-react';
import { IconLoader2 } from '@tabler/icons-react';
import Turnstile, { TurnstileHandle } from './Turnstile';

type FormStatus = 'DEFAULT' | 'SUBMITTING' | 'SUCCESS' | 'ERROR';

const ContactForm = ({ isMarketingPreferences = false }: { isMarketingPreferences: boolean }) => {
  const [formStatus, setFormStatus] = useState<FormStatus>('DEFAULT');
  const [validationErrors, setValidationErrors] = useState<string[] | []>([]);
  const [isHuman, setIsHuman] = useState<boolean>(false);
  const [tsToken, setTsToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileHandle>(null);

  const INPUT_NAMES: Record<string, string> = {
    NAME: 'name',
    EMAIL: 'email',
    REG: 'reg',
    POSTCODE: 'post_code',
    POLICY_NO: 'policy_no',
    REASON: 'reason',
    MESSAGE: 'message',
  };

  const CONTACT_REASONS: string[] = [
    'Quote enquiry',
    'Policy enquiry',
    'Marketing preferences',
    'Document request',
    'Policy cancellation request',
    'General enquiry',
  ];

  const PLACEHOLDER_TEXT: Record<string, string> = {
    NAME: 'Enter your full name',
    EMAIL: 'Enter your email',
    REG: 'BD51 SMR',
    POSTCODE: 'Enter your post code',
    POLICY_NO: 'Enter your policy number',
    MESSAGE:
      'Please leave as much information as you can regarding your enquiry and a member of the team will get back to you shortly. Please be aware that we cannot contact you over the phone, therefore call back requests will be replied to via email.',
  };

  const VALIDATION_ERRORS: Record<string, string> = {
    TURNSTILE: 'Please verify you are human first.',
    EMAIL: 'Please enter a valid email address.',
    REG: 'Registration must be between 2 and 8 characters.',
    SERVER_ERROR: 'Please check all fields and try again.',
  };

  type ContactFormData = {
    [INPUT_NAMES.NAME]: string;
    [INPUT_NAMES.EMAIL]: string;
    [INPUT_NAMES.REG]?: string;
    [INPUT_NAMES.POSTCODE]?: string;
    [INPUT_NAMES.POLICY_NO]: string;
    [INPUT_NAMES.REASON]: string;
    [INPUT_NAMES.MESSAGE]: string;
  };

  const validateForm = (): boolean => {
    // Add any additional client validation logic here.
    // Currently it is mostly handled by HTML5/browser
    // and server with the exception of ReCaptcha
    const errors = [];
    if (!isHuman || !tsToken) errors.push(VALIDATION_ERRORS.TURNSTILE);
    if (errors.length) {
      setFormStatus('ERROR');
      setValidationErrors(errors);
    }

    return errors.length === 0;
  };

  const handleSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.target as HTMLFormElement);
    formData.delete('cf-turnstile-response');
    const data = Object.fromEntries(formData.entries()) as ContactFormData;
    const isValid = validateForm();
    if (!isValid) return;

    setFormStatus('SUBMITTING');
    setValidationErrors([]);

    const response = await fetch('/builder-home-rebrand/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token: tsToken, ...data }),
    });
    if (!response.ok) {
      const errors = [VALIDATION_ERRORS.SERVER_ERROR];
      const res = await response.json();
      if (Object.prototype.hasOwnProperty.call(res, 'validationErrors')) errors.push(...res.validationErrors);
      setFormStatus('ERROR');
      setIsHuman(false);
      setTsToken(null);
      turnstileRef.current?.reset();
      return setValidationErrors(errors);
    }
    setFormStatus('SUCCESS');
  };

  const handleTurnstileCallback = useCallback((token: string) => {
    setIsHuman(true);
    setTsToken(token);
  }, []);

  const handleTurnstileErrorCallback = useCallback(() => {
    setIsHuman(false);
    setTsToken(null);
  }, []);

  const handleTurnstileExpiredCallback = useCallback((token: string) => {
    setIsHuman(false);
    setTsToken((currentToken) => (currentToken === token ? null : currentToken));
  }, []);

  return (
    <form className="gap-regular flex w-full max-w-lg flex-col" onSubmit={handleSubmit}>
      <fieldset className="gap-regular flex flex-col lg:flex-row">
        <legend className="hidden">User details:</legend>
        <div className="w-full">
          <label className="tc-label" htmlFor={INPUT_NAMES.NAME}>
            Name
            <span aria-hidden={true} className="text-primary">
              {' '}
              *
            </span>
          </label>
          <input
            className="tc-input max-h-60"
            type="text"
            name={INPUT_NAMES.NAME}
            id={INPUT_NAMES.NAME}
            placeholder={PLACEHOLDER_TEXT.NAME}
            required
          />
        </div>

        <div className="w-full">
          <label className="tc-label" htmlFor={INPUT_NAMES.EMAIL}>
            Email
            <span aria-hidden={true} className="text-primary">
              {' '}
              *
            </span>
          </label>
          <input
            className="tc-input max-h-60"
            type="text"
            name={INPUT_NAMES.EMAIL}
            id={INPUT_NAMES.EMAIL}
            placeholder={PLACEHOLDER_TEXT.EMAIL}
            required
            pattern=".+@.+"
          />
        </div>
      </fieldset>

      <fieldset className="gap-regular flex flex-col lg:flex-row">
        <legend className="hidden">Registration and policy details:</legend>
        <div className="w-full">
          {isMarketingPreferences ? (
            <>
              <label className="tc-label" htmlFor={INPUT_NAMES.POSTCODE}>
                Post code
                <span aria-hidden={true} className="text-primary">
                  {' '}
                  *
                </span>
              </label>
              <input
                className="tc-input"
                type="text"
                name={INPUT_NAMES.POSTCODE}
                id={INPUT_NAMES.POSTCODE}
                placeholder={PLACEHOLDER_TEXT.POSTCODE}
                required
              />
            </>
          ) : (
            <RegPlateInput
              label="Enter your Registration number"
              inputName={INPUT_NAMES.REG}
              className="bg-neutral !py-[9px] !pr-3"
              pattern="[0-9a-zA-Z\s]+"
              minLength={2}
              maxLength={8}
            />
          )}
        </div>
        <div className="w-full">
          <label className="tc-label" htmlFor={INPUT_NAMES.POLICY_NO}>
            Policy number (if applicable)
          </label>
          <input
            className="tc-input max-h-60"
            type="text"
            name={INPUT_NAMES.POLICY_NO}
            id={INPUT_NAMES.POLICY_NO}
            placeholder={PLACEHOLDER_TEXT.POLICY_NO}
          />
        </div>
      </fieldset>

      <fieldset className="gap-regular flex flex-col">
        <legend className="hidden">Contact information:</legend>

        <div className="w-full">
          <label className="tc-label" htmlFor={INPUT_NAMES.REASON}>
            Reason for contact
            <span aria-hidden={true} className="text-primary">
              {' '}
              *
            </span>
          </label>
          <div className="bg-neutral relative rounded-lg">
            <select
              id={INPUT_NAMES.REASON}
              name={INPUT_NAMES.REASON}
              className="tc-input max-h-60 cursor-pointer appearance-none bg-transparent"
              required
              defaultValue={isMarketingPreferences ? 'Marketing preferences' : ''}
            >
              <option value="" disabled aria-disabled>
                Please select
              </option>
              {CONTACT_REASONS.map((value, i) => (
                <option key={i} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <div className="tc-select-icon pointer-events-none" aria-hidden={true}>
              <IconChevronDown />
            </div>
          </div>
        </div>

        <div className="w-full">
          <label className="tc-label" htmlFor={INPUT_NAMES.MESSAGE}>
            Your message
            <span aria-hidden={true} className="text-primary">
              {' '}
              *
            </span>
          </label>
          <textarea
            rows={6}
            className="tc-input w-full"
            name={INPUT_NAMES.MESSAGE}
            id={INPUT_NAMES.MESSAGE}
            placeholder={PLACEHOLDER_TEXT.MESSAGE}
            aria-label={PLACEHOLDER_TEXT.MESSAGE}
            required
          />
        </div>
      </fieldset>

      <button
        id="contact-submit"
        type="submit"
        disabled={formStatus !== 'DEFAULT' && formStatus !== 'ERROR'}
        className="tc-submit-button relative"
      >
        Send Message{' '}
        {formStatus === 'SUBMITTING' ? (
          <IconLoader2 aria-hidden={true} className="absolute top-0 right-[1em] bottom-0 m-auto inline animate-spin" />
        ) : null}
      </button>

      <Turnstile
        callback={handleTurnstileCallback}
        errorCallback={handleTurnstileErrorCallback}
        expiredCallback={handleTurnstileExpiredCallback}
      />

      <div className={`bg-primary-100 rounded-md ${formStatus !== 'DEFAULT' ? 'p-4' : 'p-0'}`}>
        <div role="alert">
          {formStatus === 'SUBMITTING' && (
            <p>
              <strong>Submitting, please wait</strong>
            </p>
          )}
          {formStatus === 'SUCCESS' && (
            <p>
              <strong>
                Thank you for your enquiry.
                <br />
                Someone will be in touch with you shortly.
                <br />
                <span className="text-sm">If you would like to send another enquiry, please refresh this page.</span>
              </strong>
            </p>
          )}
          {formStatus === 'ERROR' && (
            <p>
              <strong className="text-error">There was an error submitting the form.</strong>
            </p>
          )}

          <ul className="text-error mt-0 list-none p-0 text-sm" aria-live="polite">
            {validationErrors.map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        </div>
      </div>
    </form>
  );
};

export default ContactForm;
